/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { FontAwesome } from '@expo/vector-icons';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import DatePicker from '@components/form/DatePicker';
import DateRangePicker from '@components/form/DateRangePicker';
import SociollaMultiDatePicker from '@components/sociolla/SociollaMultiDatePicker';
import MoreButton from '@components/navbar/MoreButton';
import { SociollaForm } from '@components/sociolla/form';
import AccountGroupSearch from '@components/form/search/AccountGroupSearch';
import ModalUpload from '@components/modal/ModalUpload';
import ModalTopRounded from '@components/modal/ModalTopRounded';
import SelectProductTable from '@components/form/SelectProductTable';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ItemDetail from '@components/global/ItemDetail';
import * as Layout from '@components/screen/Layout';
import { isWeb, isMobileWeb } from '@global/screens';
import Image from '@components/global/Image';
import { hasNoAccess, parseDate } from '@global/helpers';
import { formatPeriod, toMidnightUTC } from './utils';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import importTemplate from '@/assets/csv/template-import-promotion-account-products';

const defaultPhoto = require('@/assets/img/default/image-default.png');

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10;

const FormValidationSchema = Yup.object().shape({
  name: Yup.string().required('Promotion name is required.'),
  account_group: Yup.object()
    .nullable()
    .required('Account group is required.')
    .test('account_group-required', 'Account group is required.', (val) =>
      val ? Object.keys(val).length > 0 : false,
    ),
  period: Yup.object()
    .nullable()
    .when('multiple_type', {
      is: (val) => val !== 'multiple',
      then: (schema) =>
        schema
          .required('Period is required.')
          .test('period-required', 'Period start is required.', (val) =>
            val ? Boolean(val.start) : false,
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  period_dates: Yup.array().when('multiple_type', {
    is: 'multiple',
    then: (schema) => schema.min(1, 'Period is required.'),
    otherwise: (schema) => schema,
  }),
});

const PRODUCT_LIMIT = 10;

const productTableStructure = {
  image: {
    head: 'Image',
    body: 'image',
    type: 'slot',
    width: '80px',
  },
  product_name: {
    head: 'Product Name',
    body: 'product_name',
    type: 'slot',
  },
  action: {
    head: '',
    body: 'action',
    type: 'slot',
    width: '50px',
  },
};

const PromotionAccountFormFields = ({
  navigation,
  isEdit,
  onBack,
  saving,
  deleting,
  handleDelete,
  products,
  filteredProducts,
  paginatedProducts,
  productSearch,
  productSkip,
  handleSearchProduct,
  removeProduct,
  setShowChoiceModal,
  isPolling,
  showMultiDatePicker,
  setShowMultiDatePicker,
}) => {
  const { values, errors, touched, handleChange, setFieldValue, handleSubmit } =
    useFormikContext();
  return (
    <>
      {isWeb ? (
        <Layout.Wrapper white={false}>
          <Layout.Back
            title="Promotion Account"
            onCallback={() =>
              onBack ? onBack() : navigation.navigate('PromotionAccountScreen')
            }
          />
          <Layout.Title
            title={isEdit ? 'Edit Promotion Account' : 'Add Promotion Account'}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {isEdit ? (
                <Button
                  type="danger"
                  rounded={true}
                  buttonStyle={{ width: 100, marginRight: 8 }}
                  title={
                    <SociollaText
                      type="bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: 0.5,
                        color: Colors.white,
                      }}>
                      DELETE
                    </SociollaText>
                  }
                  onPress={handleDelete}
                  loading={deleting}
                />
              ) : null}
              <Button
                rounded={true}
                buttonStyle={{ width: 100 }}
                title={
                  <SociollaText
                    type="bold"
                    style={{
                      fontSize: 14,
                      letterSpacing: 0.5,
                      color: Colors.white,
                    }}>
                    {isEdit ? 'UPDATE' : 'ADD'}
                  </SociollaText>
                }
                onPress={handleSubmit}
                loading={saving}
              />
            </View>
          </Layout.Title>
        </Layout.Wrapper>
      ) : null}

      <Layout.Wrapper rounded>
        <ScrollView>
          <Layout.FormWrapper
            style={{
              width: isWeb ? 600 : '100%',
              paddingHorizontal: isWeb ? 20 : 15,
              paddingTop: 20,
              marginBottom: 15,
            }}>
            <Layout.FormGroup>
              <Input
                label="Promotion Name"
                isRequired={true}
                value={values.name}
                onChangeText={handleChange('name')}
                placeholder="Enter promotion name"
                hasError={touched.name && errors.name ? errors.name : null}
              />
            </Layout.FormGroup>

            <Layout.FormGroup>
              <AccountGroupSearch
                value={values.account_group}
                isRequired={true}
                hasError={
                  errors.account_group && touched.account_group
                    ? errors.account_group
                    : null
                }
                onChange={(value) => setFieldValue('account_group', value)}
              />
            </Layout.FormGroup>

            <Layout.FormGroup>
              <SociollaText style={styles.fieldLabel}>
                Period{' '}
                <SociollaText style={{ color: Colors.red0 }}>*</SociollaText>
              </SociollaText>

              <View style={styles.radioRow}>
                {[
                  { key: 'single', label: 'Single Days' },
                  { key: 'multiple', label: 'Multiple Days' },
                  { key: 'range', label: 'Range' },
                ].map(({ key, label }) => (
                  <TouchableOpacity
                    key={key}
                    activeOpacity={0.7}
                    style={styles.radioOption}
                    onPress={() => {
                      setFieldValue('multiple_type', key);
                      setFieldValue('period', null);
                      setFieldValue('period_dates', []);
                    }}>
                    <View
                      style={[
                        styles.radioCircle,
                        values.multiple_type === key &&
                          styles.radioCircleActive,
                      ]}>
                      {values.multiple_type === key ? (
                        <View style={styles.radioDot} />
                      ) : null}
                    </View>
                    <SociollaText style={styles.radioLabel}>
                      {label}
                    </SociollaText>
                  </TouchableOpacity>
                ))}
              </View>

              {values.multiple_type === 'single' ? (
                <DatePicker
                  hideLabel={true}
                  value={values.period?.start || ''}
                  placeholder="Select date"
                  onChange={(date) => {
                    const iso = toMidnightUTC(
                      date instanceof Date ? date : new Date(date),
                    );
                    setFieldValue('period', { start: iso, end: iso });
                  }}
                />
              ) : null}

              {values.multiple_type === 'range' ? (
                <DateRangePicker
                  hideLabel={true}
                  value={{
                    start: values.period?.start || null,
                    end: values.period?.end || null,
                  }}
                  placeholder="Select date range"
                  onChange={(range) =>
                    setFieldValue('period', {
                      start: toMidnightUTC(
                        range.start instanceof Date
                          ? range.start
                          : new Date(range.start),
                      ),
                      end: toMidnightUTC(
                        range.end instanceof Date
                          ? range.end
                          : new Date(range.end),
                      ),
                    })
                  }
                />
              ) : null}

              {values.multiple_type === 'multiple' ? (
                <>
                  <View style={{ position: 'relative' }}>
                    <TouchableOpacity
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                        zIndex: 2,
                        elevation: 2,
                      }}
                      activeOpacity={1}
                      onPress={() => setShowMultiDatePicker(true)}
                    />
                    <Input
                      hideLabel={true}
                      editable={false}
                      value={
                        values.period_dates?.length
                          ? values.period_dates
                              .map((d) => parseDate(d, 'd MMM yyyy'))
                              .join(', ')
                          : ''
                      }
                      placeholder="Select dates"
                      rightIcon={
                        <Image
                          style={{ width: 23, height: 16, marginRight: -10 }}
                          width={23}
                          height={16}
                          svg={true}
                          source={require('@assets/img/icons/menu/calendar.svg')}
                        />
                      }
                    />
                  </View>
                  <SociollaMultiDatePicker
                    visible={showMultiDatePicker}
                    values={values.period_dates || []}
                    onClose={() => setShowMultiDatePicker(false)}
                    onChange={(dates) => {
                      setFieldValue('period_dates', dates);
                      setShowMultiDatePicker(false);
                    }}
                  />
                </>
              ) : null}

              {(touched.period &&
                errors.period &&
                typeof errors.period === 'string') ||
              (touched.period_dates && errors.period_dates) ? (
                <SociollaText style={styles.errorText}>
                  {errors.period || errors.period_dates}
                </SociollaText>
              ) : null}
            </Layout.FormGroup>
          </Layout.FormWrapper>

          <View
            style={{ paddingHorizontal: isWeb ? 14 : 10, paddingBottom: 16 }}>
            <TitleSubSection
              title="PRODUCT"
              button={{
                label: 'Add Product',
                callback: () => setShowChoiceModal(true),
              }}
            />

            {isPolling ? (
              <View style={styles.pollingBanner}>
                <SociollaText style={styles.pollingText}>
                  Importing products, please wait...
                </SociollaText>
              </View>
            ) : null}

            {products.length === 0 ? (
              <View style={styles.emptyState}>
                <Image
                  source={require('@assets/img/icons/no-product.svg')}
                  style={styles.emptyIcon}
                  width={60}
                  height={60}
                  svg={true}
                />
                <SociollaText style={styles.emptyText}>
                  No product found
                </SociollaText>
              </View>
            ) : isWeb ? (
              <Table
                structures={productTableStructure}
                data={paginatedProducts}
                withFetch={false}
                hasNoHide={true}
                showFilter={false}
                withoutShadow={true}
                searchBorder={true}
                totalData={filteredProducts.length}
                skip={productSkip}
                limit={PRODUCT_LIMIT}
                searchKey="product_name"
                searchPlaceholder="Search"
                searchValue={productSearch}
                onSearch={(val) => handleSearchProduct(val, 0)}
                onLoadMore={(skip) => handleSearchProduct(productSearch, skip)}
                slot={{
                  image: ({ data }) => (
                    <Image
                      source={data.image ? { uri: data.image } : defaultPhoto}
                      style={{ width: 36, height: 36, borderRadius: 4 }}
                      width={36}
                      height={36}
                      isPhoto={true}
                    />
                  ),
                  product_name: ({ data }) => (
                    <SociollaText style={{ fontSize: 13 }}>
                      [{data.combination_reference}] {data.product_name}
                    </SociollaText>
                  ),
                  action: ({ data }) => (
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => removeProduct(data.combination_reference)}>
                      <FontAwesome
                        name="trash"
                        size={15}
                        color={Colors.grey1}
                      />
                    </TouchableOpacity>
                  ),
                }}
              />
            ) : (
              <TableMobile
                searchKey="product_name"
                placeholder="Search"
                searchValue={productSearch}
                onSearch={(val) => handleSearchProduct(val, 0)}
                skip={productSkip}
                limit={PRODUCT_LIMIT}
                tableData={paginatedProducts}
                totalData={filteredProducts.length}
                withLoadMore={true}
                onLoadMore={(skip) => handleSearchProduct(productSearch, skip)}
                renderItem={({ data, index }) => (
                  <Layout.ItemMobile key={index}>
                    <View
                      style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Image
                        source={data.image ? { uri: data.image } : defaultPhoto}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 4,
                          marginRight: 10,
                        }}
                        width={36}
                        height={36}
                        isPhoto={true}
                      />
                      <View style={{ flex: 1 }}>
                        <SociollaText type="medium" style={{ fontSize: 13 }}>
                          [{data.combination_reference}] {data.product_name}
                        </SociollaText>
                      </View>
                      <TouchableOpacity
                        activeOpacity={0.7}
                        onPress={() =>
                          removeProduct(data.combination_reference)
                        }>
                        <FontAwesome
                          name="trash"
                          size={15}
                          color={Colors.grey1}
                        />
                      </TouchableOpacity>
                    </View>
                  </Layout.ItemMobile>
                )}
              />
            )}
          </View>
        </ScrollView>
      </Layout.Wrapper>

      {!isWeb ? (
        <View style={buttonFloating}>
          <Button
            rounded={true}
            buttonStyle={{ width: '100%' }}
            title={
              <SociollaText
                type="bold"
                style={{
                  fontSize: 14,
                  letterSpacing: 0.5,
                  color: Colors.white,
                }}>
                {isEdit ? 'UPDATE' : 'ADD'}
              </SociollaText>
            }
            onPress={handleSubmit}
            loading={saving}
          />
        </View>
      ) : null}
    </>
  );
};

const PromotionAccountFormScreen = (props) => {
  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);
  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSkip, setProductSkip] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showMultiDatePicker, setShowMultiDatePicker] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showProductTable, setShowProductTable] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [promotionAccountId, setPromotionAccountId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Keep a ref to the polling timer so we can clear it on unmount
  const pollTimerRef = useRef(null);

  const getDetail = useApi(MsB2b.getPromotionAccountById);
  const deleteApi = useApi(MsB2b.deletePromotionAccount);
  const duplicateApi = useApi(MsB2b.duplicatePromotionAccount);

  const fetchDetail = async (id) => {
    const response = await getDetail.request(id);
    if (response?.data?.data) {
      const result = response.data.data;
      setDetailData(result);
      setProducts(result.products || []);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setIsViewMode(false);
      setDetailData({});
      setProducts([]);
      if (props?.route?.params?.id) {
        setIsEdit(true);
        setIsViewMode(!!props.route.params.viewMode);
        setPromotionAccountId(props.route.params.id);
        fetchDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
      // Clear any running poll on unmount
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    if (
      mounted &&
      !isWeb &&
      isViewMode &&
      isEdit &&
      detailData?._id &&
      hasAccess
    ) {
      setTimeout(() => {
        props.navigation.setOptions({
          headerRight: () => (
            <MoreButton
              menu={[
                {
                  type: 'function',
                  name: 'Edit',
                  callback: () =>
                    props.navigation.navigate('PromotionAccountFormScreen', {
                      titleScreen: detailData.name,
                      id: detailData._id,
                    }),
                },
                {
                  type: 'function',
                  name: 'Duplicate',
                  callback: () => onDuplicate(),
                },
              ]}
              {...props}
            />
          ),
        });
      }, 250);
    }
    return () => {
      mounted = false;
    };
  }, [isViewMode, isEdit, detailData, hasAccess]);

  /**
   * Poll the detail endpoint until updated_at changes (meaning the async
   * worker has stamped the document) or we exhaust MAX_ATTEMPTS.
   *
   * @param {string} id                  - promotion account id
   * @param {string|Date} previousUpdatedAt - updated_at value before the import
   */
  const pollForUpdates = (id, previousUpdatedAt) => {
    let attempts = 0;
    setIsPolling(true);

    const attempt = async () => {
      attempts += 1;
      try {
        const response = await getDetail.request(id);
        const result = response?.data?.data;
        if (result) {
          const newUpdatedAt = result.updated_at;
          if (
            newUpdatedAt &&
            String(newUpdatedAt) !== String(previousUpdatedAt)
          ) {
            // Worker finished — update state and stop polling
            setDetailData(result);
            setProducts(result.products || []);
            setIsPolling(false);
            Toast.show({
              text1: 'Products imported successfully.',
              type: 'success',
            });
            return;
          }
        }
      } catch (_err) {
        // silently ignore fetch errors during polling
      }

      if (attempts < POLL_MAX_ATTEMPTS) {
        pollTimerRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
      } else {
        // Give up polling — tell the user to refresh manually
        setIsPolling(false);
        Toast.show({
          text1:
            'Import is taking longer than expected. Please refresh the page once you receive the email.',
          type: 'info',
        });
      }
    };

    pollTimerRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
  };

  const filteredProducts = products.filter((p) => {
    if (!productSearch) {
      return true;
    }
    const q = productSearch.toLowerCase();
    return (
      p.product_name?.toLowerCase().includes(q) ||
      p.combination_reference?.toLowerCase().includes(q) ||
      p.brand_name?.toLowerCase().includes(q)
    );
  });

  const paginatedProducts = filteredProducts.slice(
    productSkip,
    productSkip + PRODUCT_LIMIT,
  );

  const handleSearchProduct = (val, newSkip) => {
    setProductSkip(newSkip);
    setProductSearch(val);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const ag = values.account_group;
      let periodStart = '';
      let periodEnd = '';
      if (values.multiple_type === 'multiple' && values.period_dates?.length) {
        const sorted = [...values.period_dates].sort(
          (a, b) => new Date(a) - new Date(b),
        );
        periodStart = toMidnightUTC(
          sorted[0] instanceof Date ? sorted[0] : new Date(sorted[0]),
        );
        periodEnd = toMidnightUTC(
          sorted[sorted.length - 1] instanceof Date
            ? sorted[sorted.length - 1]
            : new Date(sorted[sorted.length - 1]),
        );
      } else {
        periodStart = values.period?.start || '';
        periodEnd = values.period?.end || '';
      }
      const payload = {
        name: values.name,
        account_group: ag
          ? { id: ag._id || ag.id, code: ag.code, name: ag.code }
          : null,
        period: { start: periodStart, end: periodEnd },
        period_type: values.multiple_type,
        dates:
          values.multiple_type === 'multiple'
            ? [...values.period_dates]
                .sort((a, b) => new Date(a) - new Date(b))
                .map((d) => toMidnightUTC(d instanceof Date ? d : new Date(d)))
            : [],
        products,
      };
      let response;
      if (isEdit) {
        response = await MsB2b.updatePromotionAccount(
          props.route.params.id,
          payload,
        );
      } else {
        response = await MsB2b.createPromotionAccount(payload);
      }
      if (response?.data?.success) {
        Toast.show({
          text1: isEdit ? 'Updated successfully.' : 'Created successfully.',
          type: 'success',
        });
        props.navigation.replace('PromotionAccountFormScreen', {
          titleScreen: response?.data?.data.name,
          id: response?.data?.data._id,
          viewMode: true,
        });
      }
    } catch (error) {
      Toast.show({
        text1:
          error?.response?.data?.message ||
          error?.message ||
          'A system error occurred',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteApi.request(props.route.params.id);
      Toast.show({ text1: 'Deleted successfully.', type: 'success' });
      props.navigation.goBack();
    } catch (error) {
      Toast.show({
        text1:
          error?.response?.data?.message ||
          error?.message ||
          'A system error occurred',
        type: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const onDuplicate = async () => {
    setDuplicating(true);
    try {
      const response = await duplicateApi.request(props.route.params.id);
      if (response?.data?.data?._id) {
        props.navigation.replace('PromotionAccountFormScreen', {
          titleScreen: response.data.data.name,
          id: response.data.data._id,
        });
      } else {
        Toast.show({ text1: 'Failed to duplicate.', type: 'error' });
      }
    } catch (error) {
      Toast.show({
        text1:
          error?.response?.data?.message ||
          error?.message ||
          'A system error occurred',
        type: 'error',
      });
    } finally {
      setDuplicating(false);
    }
  };

  const onSelectProduct = (product) => {
    const already = products.find(
      (p) =>
        String(p.combination_id) === String(product.product_combination_id),
    );
    if (already) {
      Toast.show({ text1: 'Product already added.', type: 'error' });
      return;
    }
    setProducts([
      ...products,
      {
        product_id: product.id,
        product_name: product.name,
        combination_id: product.product_combination_id,
        combination_reference: product.reference,
        image: product.image || '',
        brand_name: product.brand.name,
        // is_premium removed
      },
    ]);
    setProductSkip(0);
  };

  const removeProduct = (combinationReference) => {
    setProducts(
      products.filter((p) => p.combination_reference !== combinationReference),
    );
    setProductSkip(0);
  };

  const handleImportProducts = async (url) => {
    try {
      if (!promotionAccountId) {
        Toast.show({
          text1:
            'Please save the promotion account first before importing products.',
          type: 'error',
        });
        return;
      }

      // Snapshot updated_at BEFORE triggering the import so the poller can
      // detect when the worker stamps the document (even if 0 products added).
      const previousUpdatedAt = detailData?.updated_at;

      await MsB2b.importPromotionAccountProducts({
        url,
        promotion_account_id: promotionAccountId,
      });

      Toast.show({
        text1: 'Import in progress, the result will be sent to your email.',
        type: 'success',
      });
      setShowImportModal(false);

      // Poll until worker finishes instead of fetching immediately
      pollForUpdates(promotionAccountId, previousUpdatedAt);
    } catch (error) {
      Toast.show({
        text1:
          error?.response?.data?.message ||
          error?.message ||
          'A system error occurred',
        type: 'error',
      });
    }
  };

  if (isEdit && getDetail.loading) {
    return (
      <Layout.Container>
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      </Layout.Container>
    );
  }

  if (isViewMode && isEdit && detailData && Object.keys(detailData).length) {
    const isActive = detailData?.is_active === true;
    return (
      <Layout.Container>
        {isWeb ? (
          <Layout.Wrapper white={false}>
            <Layout.Back
              title="Promotion Account"
              onCallback={() =>
                props.navigation.navigate('PromotionAccountScreen')
              }
            />
            <Layout.Title title={detailData.name || 'Promotion Account'}>
              {hasAccess ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Button
                    rounded={true}
                    buttonStyle={{ width: 110, marginRight: 8 }}
                    loading={duplicating}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        DUPLICATE
                      </SociollaText>
                    }
                    onPress={onDuplicate}
                  />
                  <Button
                    rounded={true}
                    buttonStyle={{ width: 100 }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        EDIT
                      </SociollaText>
                    }
                    onPress={() =>
                      props.navigation.navigate('PromotionAccountFormScreen', {
                        titleScreen: detailData.name,
                        id: props.route.params.id,
                      })
                    }
                  />
                </View>
              ) : null}
            </Layout.Title>
          </Layout.Wrapper>
        ) : null}
        <Layout.Wrapper rounded>
          <ScrollView>
            <Layout.FormWrapper
              style={{
                width: isWeb ? 600 : '100%',
                paddingHorizontal: isWeb ? 20 : 15,
                paddingTop: 20,
                paddingBottom: 10,
              }}>
              <ItemDetail
                name="Promotion Name"
                value={detailData?.name || '-'}
              />
              <ItemDetail
                name="Account Group"
                value={
                  detailData?.account_group?.name ||
                  detailData?.account_group?.code ||
                  '-'
                }
              />
              <ItemDetail
                name="Period"
                value={formatPeriod(
                  detailData?.period,
                  detailData?.period_type,
                  detailData?.dates,
                )}
              />
              <ItemDetail
                name="Status"
                valueComponent={
                  <View
                    style={{
                      backgroundColor: isActive
                        ? Colors.statusActiveBg
                        : Colors.statusInactiveBg,
                      borderRadius: 4,
                      paddingHorizontal: 8,
                      paddingVertical: 2,
                      marginLeft: 20,
                      alignSelf: 'flex-start',
                    }}>
                    <SociollaText
                      style={{
                        color: isActive ? Colors.green1 : Colors.grey1,
                        fontSize: 12,
                      }}>
                      {isActive ? 'Active' : 'Inactive'}
                    </SociollaText>
                  </View>
                }
              />
              {detailData?.updated_by?.name || detailData?.updated_at ? (
                <ItemDetail
                  name="Last Update, by"
                  value={`${
                    detailData?.updated_at
                      ? parseDate(detailData.updated_at, 'dd MMM yyyy')
                      : '-'
                  }${
                    detailData?.updated_by?.name
                      ? ` | ${detailData.updated_by.name}`
                      : ''
                  }`}
                />
              ) : null}
            </Layout.FormWrapper>

            <View
              style={{ paddingHorizontal: isWeb ? 14 : 10, paddingBottom: 16 }}>
              <TitleSubSection title="PRODUCT" />

              {products.length === 0 ? (
                <View style={styles.emptyState}>
                  <SociollaText style={styles.emptyText}>
                    No product found
                  </SociollaText>
                </View>
              ) : isWeb ? (
                <Table
                  structures={{
                    image: {
                      head: 'Image',
                      body: 'image',
                      type: 'slot',
                      width: '80px',
                    },
                    reference: {
                      head: 'Reference',
                      body: 'combination_reference',
                      type: 'slot',
                      width: '160px',
                    },
                    product_name: {
                      head: 'Product Name',
                      body: 'product_name',
                      type: 'slot',
                    },
                  }}
                  data={paginatedProducts}
                  withFetch={false}
                  hasNoHide={true}
                  showFilter={false}
                  withoutShadow={true}
                  totalData={filteredProducts.length}
                  skip={productSkip}
                  limit={PRODUCT_LIMIT}
                  onSearch={(val) => handleSearchProduct(val, 0)}
                  onLoadMore={(skip) =>
                    handleSearchProduct(productSearch, skip)
                  }
                  slot={{
                    image: ({ data }) => (
                      <Image
                        source={data.image ? { uri: data.image } : defaultPhoto}
                        style={{ width: 36, height: 36, borderRadius: 4 }}
                        width={36}
                        height={36}
                        isPhoto={true}
                      />
                    ),
                    reference: ({ data }) => (
                      <SociollaText
                        style={{ fontSize: 13, color: Colors.grey1 }}>
                        {data.combination_reference || '-'}
                      </SociollaText>
                    ),
                    product_name: ({ data }) => (
                      <SociollaText style={{ fontSize: 13 }}>
                        {data.product_name}
                      </SociollaText>
                    ),
                  }}
                />
              ) : (
                <TableMobile
                  tableData={paginatedProducts}
                  totalData={filteredProducts.length}
                  skip={productSkip}
                  limit={PRODUCT_LIMIT}
                  withLoadMore={true}
                  onLoadMore={(skip) =>
                    handleSearchProduct(productSearch, skip)
                  }
                  renderItem={({ data, index }) => (
                    <Layout.ItemMobile key={index}>
                      <View
                        style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Image
                          source={
                            data.image ? { uri: data.image } : defaultPhoto
                          }
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 4,
                            marginRight: 10,
                          }}
                          width={36}
                          height={36}
                          isPhoto={true}
                        />
                        <View style={{ flex: 1 }}>
                          {data.combination_reference ? (
                            <SociollaText
                              style={{
                                fontSize: 11,
                                color: Colors.grey1,
                                marginBottom: 2,
                              }}>
                              {data.combination_reference}
                            </SociollaText>
                          ) : null}
                          <SociollaText type="medium" style={{ fontSize: 13 }}>
                            {data.product_name}
                          </SociollaText>
                        </View>
                      </View>
                    </Layout.ItemMobile>
                  )}
                />
              )}
            </View>
          </ScrollView>
        </Layout.Wrapper>
        {!isWeb && hasAccess ? (
          <View style={buttonFloating}>
            <Button
              rounded={true}
              buttonStyle={{ width: '100%' }}
              title={
                <SociollaText
                  type="bold"
                  style={{
                    fontSize: 14,
                    letterSpacing: 0.5,
                    color: Colors.white,
                  }}>
                  EDIT
                </SociollaText>
              }
              onPress={() =>
                props.navigation.navigate('PromotionAccountFormScreen', {
                  titleScreen: detailData.name,
                  id: props.route.params.id,
                })
              }
            />
          </View>
        ) : null}
      </Layout.Container>
    );
  }

  const initialValues = {
    name: isEdit ? detailData?.name || '' : '',
    account_group: isEdit ? detailData?.account_group || null : null,
    multiple_type: isEdit ? detailData?.period_type || 'range' : 'range',
    period:
      isEdit && detailData?.period_type !== 'multiple'
        ? {
            start: detailData?.period?.start || '',
            end: detailData?.period?.end || '',
          }
        : null,
    period_dates:
      isEdit && detailData?.period_type === 'multiple'
        ? (detailData?.dates || []).map((d) => new Date(d))
        : [],
  };

  return (
    <Layout.Container>
      {(isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <SociollaForm
          enableReinitialize
          initialValues={initialValues}
          validationSchema={FormValidationSchema}
          onSubmit={handleSave}>
          <PromotionAccountFormFields
            navigation={props.navigation}
            isEdit={isEdit}
            onBack={() =>
              // On edit, return to the record's detail (view mode); on add, list.
              isEdit
                ? props.navigation.navigate('PromotionAccountFormScreen', {
                    ...props.route.params,
                    viewMode: true,
                  })
                : props.navigation.navigate('PromotionAccountScreen')
            }
            saving={saving}
            deleting={deleting}
            handleDelete={handleDelete}
            products={products}
            filteredProducts={filteredProducts}
            paginatedProducts={paginatedProducts}
            productSearch={productSearch}
            productSkip={productSkip}
            handleSearchProduct={handleSearchProduct}
            removeProduct={removeProduct}
            setShowChoiceModal={setShowChoiceModal}
            isPolling={isPolling}
            showMultiDatePicker={showMultiDatePicker}
            setShowMultiDatePicker={setShowMultiDatePicker}
          />
        </SociollaForm>
      ) : null}

      {/* Choice modal */}
      <ModalTopRounded
        title="Add Product"
        visible={showChoiceModal}
        maxWidth={isWeb && !isMobileWeb ? 500 : '100%'}
        onClose={() => setShowChoiceModal(false)}>
        <View style={styles.marketCreate}>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => {
              setShowChoiceModal(false);
              setShowProductTable(true);
            }}>
            <Image
              source={require('@assets/img/icons/add-manually-market-type.png')}
              style={{ width: 43, height: 43 }}
              width={43}
              height={43}
            />
            <SociollaText style={styles.itemText}>Add Manually</SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => {
              setShowChoiceModal(false);
              setShowImportModal(true);
            }}>
            <Image
              source={require('@assets/img/icons/add-from-csv.png')}
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
            />
            <SociollaText style={styles.itemText}>Import CSV</SociollaText>
          </TouchableOpacity>
        </View>
      </ModalTopRounded>

      {/* Product table modal */}
      <SelectProductTable
        show={showProductTable}
        onClose={() => setShowProductTable(false)}
        onCallback={(product) => {
          onSelectProduct(product);
          setShowProductTable(false);
        }}
      />

      {/* Import CSV modal */}
      {showImportModal ? (
        <ModalUpload
          show={showImportModal}
          onClose={() => setShowImportModal(false)}
          templateUrl={importTemplate}
          onUpload={handleImportProducts}
          autoUploadS3={true}
          name="promotion_account_products"
          validHeader={['SKU']}
        />
      ) : null}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  fieldLabel: {
    fontSize: 14,
    marginBottom: 8,
    fontFamily: 'figtree-medium',
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  radioOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  radioCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: Colors.grey1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  radioCircleActive: { borderColor: Colors.blue0 },
  radioDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: Colors.blue0,
  },
  radioLabel: { fontSize: 13 },
  errorText: { fontSize: 12, color: Colors.red0, marginTop: 4 },
  marketCreate: {
    width: '100%',
    paddingHorizontal: isWeb ? 25 : 15,
    paddingTop: isWeb ? 25 : 15,
    paddingBottom: isWeb ? 40 : 15,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  itemOption: {
    padding: 15,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginHorizontal: isWeb ? 15 : 10,
  },
  itemText: {
    fontSize: 14,
    marginTop: 15,
  },
  emptyState: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 50,
    backgroundColor: Colors.white,
  },
  emptyIcon: { width: 60, height: 60, marginBottom: 12 },
  emptyText: { fontSize: 14, color: Colors.grey1 },
  pollingBanner: {
    backgroundColor: Colors.pollingBannerBg,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: Colors.pollingBannerBorder,
  },
  pollingText: {
    fontSize: 13,
    color: Colors.pollingBannerText,
  },
});

export default PromotionAccountFormScreen;
